import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  BookOpen,
  Clock3,
  CreditCard,
  Landmark,
  RefreshCw,
  UserRound,
  Wallet,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  fetchPlayerOverview,
  fetchPlayerPassbook,
  fetchPlayerDeposits,
} from './services/playerDetailsService';
import type {
  PassbookItem,
  PassbookPagination,
  PassbookSummary as PassbookSummaryData,
  PlayerDepositSummary,
  PlayerOverview,
} from './types';
import PassbookSummary from './components/PassbookSummary';
import DepositSummary from './components/DepositSummary';
import SimplePassbookTable from './components/SimplePassbookTable';
import DepositTable from './components/DepositTable';
import WithdrawalSummary from './components/WithdrawalSummary';
import WithdrawalTable from './components/WithdrawalTable';
import PassbookFilters, {
  type PassbookFilter,
} from './components/PassbookFilters';
import PassbookToolbar from './components/PassbookToolbar';
import TransactionDetailsDrawer from './components/TransactionDetailsDrawer';
import PlayerAnalytics from './components/PlayerAnalytics';

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value?: string | null) => {
  if (!value) return '—';

  return new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PlayerDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playerId } = useParams<{ playerId: string }>();

  const [player, setPlayer] = useState<PlayerOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState<
    'overview' | 'passbook' | 'deposit' | 'withdrawal_completed' | 'analytics'
  >('overview');
  const [passbookItems, setPassbookItems] = useState<PassbookItem[]>([]);
  const [passbookSummary, setPassbookSummary] =
    useState<PassbookSummaryData | null>(null);
  const [passbookPagination, setPassbookPagination] =
    useState<PassbookPagination | null>(null);
  const [passbookLoading, setPassbookLoading] = useState(false);
  const [passbookError, setPassbookError] = useState('');
  const [passbookPageSize, setPassbookPageSize] = useState(20);
  const [passbookFilter, setPassbookFilter] =
    useState<PassbookFilter>('all');

  const [depositItems, setDepositItems] = useState<PassbookItem[]>([]);
  const [depositSummary, setDepositSummary] =
    useState<PlayerDepositSummary | null>(null);
  const [depositPagination, setDepositPagination] =
    useState<PassbookPagination | null>(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [depositPageSize, setDepositPageSize] = useState(20);

  const [passbookSearchInput, setPassbookSearchInput] = useState('');
  const [passbookSearch, setPassbookSearch] = useState('');
  const [passbookDateFromInput, setPassbookDateFromInput] = useState('');
  const [passbookDateToInput, setPassbookDateToInput] = useState('');
  const [passbookDateFrom, setPassbookDateFrom] = useState('');
  const [passbookDateTo, setPassbookDateTo] = useState('');

  const [selectedTransaction, setSelectedTransaction] =
    useState<PassbookItem | null>(null);
  const [transactionDrawerOpen, setTransactionDrawerOpen] =
    useState(false);

  const loadPlayer = async () => {
    if (!playerId) {
      setError('Player ID is missing.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data = await fetchPlayerOverview(playerId);
      setPlayer(data);
    } catch (err: any) {
      setPlayer(null);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load player details.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const loadPassbook = async (
    page = 1,
    pageSize = passbookPageSize,
    transactionType: PassbookFilter = passbookFilter,
    search = passbookSearch,
    dateFrom = passbookDateFrom,
    dateTo = passbookDateTo,
  ) => {
    if (!playerId) return;

    try {
      setPassbookLoading(true);
      setPassbookError('');

      const response = await fetchPlayerPassbook(
        playerId,
        page,
        pageSize,
        transactionType,
        search,
        dateFrom,
        dateTo,
      );

      setPassbookItems(response.data || []);
      setPassbookSummary(response.summary || null);
      setPassbookPagination(response.pagination || null);
    } catch (err: any) {
      setPassbookItems([]);
      setPassbookSummary(null);
      setPassbookPagination(null);
      setPassbookError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load financial passbook.',
      );
    } finally {
      setPassbookLoading(false);
    }
  };

  const loadDeposits = async (
    page = 1,
    pageSize = depositPageSize,
    search = passbookSearch,
    dateFrom = passbookDateFrom,
    dateTo = passbookDateTo,
  ) => {
    if (!playerId) return;

    try {
      setDepositLoading(true);
      setDepositError('');

      const response = await fetchPlayerDeposits(
        playerId,
        page,
        pageSize,
        search,
        dateFrom,
        dateTo,
      );

      setDepositItems(response.data || []);
      setDepositSummary(response.summary || null);
      setDepositPagination(response.pagination || null);
    } catch (err: any) {
      setDepositItems([]);
      setDepositSummary(null);
      setDepositPagination(null);
      setDepositError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load player deposit history.',
      );
    } finally {
      setDepositLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'passbook') {
      setPassbookFilter('all');
      loadPassbook(1, passbookPageSize, 'all');
    }

    if (activeTab === 'deposit') {
      loadDeposits(1, depositPageSize);
    }

    if (activeTab === 'withdrawal_completed') {
      setPassbookFilter('withdrawal_completed');
      loadPassbook(1, passbookPageSize, 'withdrawal_completed');
    }

    if (activeTab === 'analytics') {
      setPassbookFilter('all');
      loadPassbook(1, 1, 'all');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, playerId]);

  const exportCurrentPassbookPage = () => {
    if (passbookItems.length === 0) return;

    const escapeCsv = (value: unknown) => {
      const stringValue = String(value ?? '');
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const rows = passbookItems.map((item) => [
      item.created_at,
      item.description || item.type_display || item.type,
      item.before_amount ?? '',
      item.amount ?? '',
      item.after_amount ?? '',
      item.status_display || item.status || '',
      item.transaction_id || item.id,
    ]);

    const csv = [
      [
        'Date & Time',
        'Particulars',
        'Previous Balance',
        'Amount',
        'Current Balance',
        'Status',
        'Reference',
      ],
      ...rows,
    ]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `player-${playerId}-ledger-page-${
      passbookPagination?.current_page || 1
    }.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <UserRound className="h-4 w-4" />,
      enabled: true,
    },
    {
      id: 'passbook',
      label: 'Financial Passbook',
      icon: <BookOpen className="h-4 w-4" />,
      enabled: true,
    },
    {
      id: 'deposit',
      label: 'Deposits',
      icon: <Wallet className="h-4 w-4" />,
      enabled: true,
    },
    {
      id: 'withdrawal_completed',
      label: 'Withdrawals',
      icon: <Landmark className="h-4 w-4" />,
      enabled: true,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <Clock3 className="h-4 w-4" />,
      enabled: true,
    },
    {
      id: 'caller',
      label: 'Caller',
      icon: <CreditCard className="h-4 w-4" />,
      enabled: false,
    },
  ];

  const fullName =
    player?.full_name?.trim() ||
    `${player?.first_name || ''} ${player?.last_name || ''}`.trim() ||
    player?.mobile_number ||
    'Player';

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <button
                type="button"
                onClick={() => {
                    const playersBasePath =
                      location.pathname.replace(/\/[^/]+\/?$/, '');

                    navigate(playersBasePath || '/players');
                  }}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Players
              </button>

              <div className="mt-5 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <UserRound className="h-7 w-7" />
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                    Player ID #{playerId}
                  </p>

                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold text-slate-900">
                      {loading ? 'Loading player...' : fullName}
                    </h1>

                    {player && (
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          player.is_active
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {player.status_display ||
                          (player.is_active ? 'Active' : 'Inactive')}
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-slate-500">
                    Review player information and complete financial history.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={loadPlayer}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 border-t border-slate-200 pt-5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                disabled={!tab.enabled}
                onClick={() => {
                  if (
                    tab.id === 'overview' ||
                    tab.id === 'passbook' ||
                    tab.id === 'deposit' || tab.id === 'withdrawal_completed' ||
                    tab.id === 'analytics'
                  ) {
                    setActiveTab(tab.id);
                  }
                }}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                  tab.id === activeTab
                    ? 'bg-blue-600 text-white shadow-sm'
                    : tab.enabled
                      ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                }`}
              >
                {tab.icon}
                {tab.label}
                {!tab.enabled && (
                  <span className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                    Soon
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {activeTab === 'overview' ? (
        <div className="grid gap-6 xl:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
            <h2 className="text-lg font-semibold text-slate-900">
              Player Overview
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              Personal, account and access information.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                ['Player ID', player?.id ?? playerId ?? '—'],
                ['Full Name', player?.full_name || '—'],
                ['Phone', player?.mobile_number || '—'],
                ['Email', player?.email || '—'],
                ['Application', player?.app?.name || '—'],
                ['Role', player?.role?.name || '—'],
                ['Joined', formatDate(player?.date_joined)],
                ['Last Login', formatDate(player?.last_login)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                >
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    {label}
                  </p>

                  <p className="mt-2 break-words text-sm font-semibold text-slate-900">
                    {loading ? 'Loading...' : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">
                Wallet Summary
              </h2>

              <p className="mt-2 text-sm text-slate-500">
                Current wallet balances from the ledger system.
              </p>

              <div className="mt-6 rounded-2xl bg-slate-900 p-5 text-white">
                <p className="text-sm text-slate-300">Available Balance</p>
                <p className="mt-2 text-3xl font-semibold">
                  {loading
                    ? 'Loading...'
                    : formatCurrency(player?.wallet.available)}
                </p>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Locked Balance
                  </p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">
                    {loading
                      ? 'Loading...'
                      : formatCurrency(player?.wallet.locked)}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Total Balance
                  </p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">
                    {loading
                      ? 'Loading...'
                      : formatCurrency(player?.wallet.total)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">
                Account Status
              </h2>

              <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
                <span className="text-sm font-medium text-slate-600">
                  Current Status
                </span>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    player?.is_active
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {loading
                    ? 'Loading...'
                    : player?.status_display || 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Bank Details
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Saved withdrawal bank information for this player.
                </p>
              </div>

              {player?.bank_accounts?.length ? (
                <span className="self-start rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {player.bank_accounts.length}{' '}
                  {player.bank_accounts.length === 1 ? 'Account' : 'Accounts'}
                </span>
              ) : null}
            </div>

            {loading ? (
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                Loading bank details...
              </div>
            ) : !player?.bank_accounts?.length ? (
              <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                <p className="text-sm font-semibold text-slate-700">
                  No bank account added
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  This player has no saved withdrawal bank details.
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-5">
                {[...player.bank_accounts]
                  .sort(
                    (a, b) =>
                      Number(b.is_default) - Number(a.is_default),
                  )
                  .map((account) => {
                    const isVerified =
                      account.status?.toLowerCase() === 'verified';

                    const statusLabel =
                      account.status
                        ? account.status.charAt(0).toUpperCase() +
                          account.status.slice(1).toLowerCase()
                        : 'Unknown';

                    return (
                      <div
                        key={account.id}
                        className="overflow-hidden rounded-2xl border border-slate-200"
                      >
                        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                              Account Holder
                            </p>
                            <p className="mt-1 text-base font-semibold text-slate-900">
                              {account.account_holder_name || '—'}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {account.is_default && (
                              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                                Default
                              </span>
                            )}

                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                isVerified
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : account.status === 'rejected'
                                    ? 'bg-red-50 text-red-700'
                                    : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {isVerified ? '✓ ' : ''}
                              {statusLabel}
                            </span>
                          </div>
                        </div>

                        <div className="grid gap-5 p-5 md:grid-cols-2 xl:grid-cols-3">
                          {[
                            ['Bank', account.bank_name || '—'],
                            ['Branch', account.branch_name || '—'],
                            [
                              'Account Number',
                              account.account_number || '—',
                            ],
                            ['IFSC Code', account.ifsc_code || '—'],
                            [
                              'Account Type',
                              account.account_type
                                ? account.account_type.charAt(0).toUpperCase() +
                                  account.account_type.slice(1)
                                : '—',
                            ],
                            [
                              'Default Account',
                              account.is_default ? 'Yes' : 'No',
                            ],
                            ['Created', formatDate(account.created_at)],
                            ['Updated', formatDate(account.updated_at)],
                            [
                              'Verified At',
                              formatDate(account.verified_at),
                            ],
                          ].map(([label, value]) => (
                            <div
                              key={label}
                              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                            >
                              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                                {label}
                              </p>
                              <p className="mt-2 break-words text-sm font-semibold text-slate-900">
                                {String(value)}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="border-t border-slate-200 px-5 py-4">
                          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                            Verification Notes
                          </p>
                          <p className="mt-2 text-sm text-slate-700">
                            {account.verification_notes ||
                              'No verification notes available.'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          </div>
        ) : activeTab === 'analytics' ? (
          <PlayerAnalytics
            player={player}
            summary={passbookSummary}
            loading={passbookLoading}
          />
        ) : (
          <div className="space-y-6">
            {(activeTab === 'deposit' ? depositError : passbookError) && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                {activeTab === 'deposit' ? depositError : passbookError}
              </div>
            )}

            {activeTab === 'deposit' ? (
              <DepositSummary
                total={depositSummary?.total_amount ?? 0}
                successful={depositSummary?.successful_amount ?? 0}
                pending={depositSummary?.pending_amount ?? 0}
                rejected={depositSummary?.rejected_amount ?? 0}
                loading={depositLoading}
              />
            ) : activeTab === 'withdrawal_completed' ? (
              <WithdrawalSummary
                total={passbookSummary?.total_withdrawals ?? 0}
                successful={passbookItems
                  .filter((item) =>
                    ['completed', 'approved', 'success'].includes(
                      String(item.status || '').toLowerCase(),
                    ),
                  )
                  .reduce(
                    (sum, item) => sum + Math.abs(Number(item.amount || 0)),
                    0,
                  )}
                pending={passbookItems
                  .filter(
                    (item) =>
                      String(item.status || '').toLowerCase() === 'pending',
                  )
                  .reduce(
                    (sum, item) => sum + Math.abs(Number(item.amount || 0)),
                    0,
                  )}
                rejected={passbookItems
                  .filter((item) =>
                    ['rejected', 'failed', 'cancelled'].includes(
                      String(item.status || '').toLowerCase(),
                    ),
                  )
                  .reduce(
                    (sum, item) => sum + Math.abs(Number(item.amount || 0)),
                    0,
                  )}
                loading={passbookLoading}
              />
            ) : (
              <PassbookSummary
                summary={passbookSummary}
                loading={passbookLoading}
              />
            )}

            <PassbookToolbar
              searchInput={passbookSearchInput}
              dateFrom={passbookDateFromInput}
              dateTo={passbookDateToInput}
              loading={
                activeTab === 'deposit'
                  ? depositLoading
                  : passbookLoading
              }
              onSearchInputChange={setPassbookSearchInput}
              onDateFromChange={setPassbookDateFromInput}
              onDateToChange={setPassbookDateToInput}
              onApply={() => {
                const nextSearch = passbookSearchInput.trim();
                const nextDateFrom = passbookDateFromInput;
                const nextDateTo = passbookDateToInput;

                if (
                  nextDateFrom &&
                  nextDateTo &&
                  nextDateFrom > nextDateTo
                ) {
                  if (activeTab === 'deposit') {
                    setDepositError(
                      'From Date cannot be later than To Date.',
                    );
                  } else {
                    setPassbookError(
                      'From Date cannot be later than To Date.',
                    );
                  }
                  return;
                }

                setPassbookSearch(nextSearch);
                setPassbookDateFrom(nextDateFrom);
                setPassbookDateTo(nextDateTo);

                if (activeTab === 'deposit') {
                  loadDeposits(
                    1,
                    depositPageSize,
                    nextSearch,
                    nextDateFrom,
                    nextDateTo,
                  );
                } else {
                  loadPassbook(
                    1,
                    passbookPageSize,
                    passbookFilter,
                    nextSearch,
                    nextDateFrom,
                    nextDateTo,
                  );
                }
              }}
              onReset={() => {
                setPassbookSearchInput('');
                setPassbookDateFromInput('');
                setPassbookDateToInput('');
                setPassbookSearch('');
                setPassbookDateFrom('');
                setPassbookDateTo('');

                if (activeTab === 'deposit') {
                  loadDeposits(
                    1,
                    depositPageSize,
                    '',
                    '',
                    '',
                  );
                } else {
                  const resetFilter: PassbookFilter =
                    activeTab === 'withdrawal_completed'
                      ? 'withdrawal_completed'
                      : 'all';

                  setPassbookFilter(resetFilter);

                  loadPassbook(
                    1,
                    passbookPageSize,
                    resetFilter,
                    '',
                    '',
                    '',
                  );
                }
              }}
              onRefresh={() => {
                if (activeTab === 'deposit') {
                  loadDeposits(
                    depositPagination?.current_page || 1,
                    depositPageSize,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                } else {
                  loadPassbook(
                    passbookPagination?.current_page || 1,
                    passbookPageSize,
                    passbookFilter,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                }
              }}
              onExport={exportCurrentPassbookPage}
            />

            {activeTab === 'passbook' && (
              <PassbookFilters
                value={passbookFilter}
                disabled={passbookLoading}
                onChange={(filter) => {
                  setPassbookFilter(filter);

                  loadPassbook(
                    1,
                    passbookPageSize,
                    filter,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                }}
              />
            )}

            {activeTab === 'deposit' ? (
              <DepositTable
                items={depositItems}
                pagination={depositPagination}
                loading={depositLoading}
                pageSize={depositPageSize}
                onRowClick={(transaction) => {
                  setSelectedTransaction(transaction);
                  setTransactionDrawerOpen(true);
                }}
                onPageChange={(page) =>
                  loadDeposits(
                    page,
                    depositPageSize,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  )
                }
                onPageSizeChange={(pageSize) => {
                  setDepositPageSize(pageSize);

                  loadDeposits(
                    1,
                    pageSize,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                }}
              />
            ) : activeTab === 'withdrawal_completed' ? (
              <WithdrawalTable
                items={passbookItems}
                pagination={passbookPagination}
                loading={passbookLoading}
                pageSize={passbookPageSize}
                onRowClick={(transaction) => {
                  setSelectedTransaction(transaction);
                  setTransactionDrawerOpen(true);
                }}
                onPageChange={(page) =>
                  loadPassbook(
                    page,
                    passbookPageSize,
                    'withdrawal_completed',
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  )
                }
                onPageSizeChange={(pageSize) => {
                  setPassbookPageSize(pageSize);

                  loadPassbook(
                    1,
                    pageSize,
                    'withdrawal_completed',
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                }}
              />
            ) : (
              <SimplePassbookTable
                items={passbookItems}
                pagination={passbookPagination}
                loading={passbookLoading}
                pageSize={passbookPageSize}
                onRowClick={(transaction) => {
                  setSelectedTransaction(transaction);
                  setTransactionDrawerOpen(true);
                }}
                onPageChange={(page) =>
                  loadPassbook(
                    page,
                    passbookPageSize,
                    passbookFilter,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  )
                }
                onPageSizeChange={(pageSize) => {
                  setPassbookPageSize(pageSize);

                  loadPassbook(
                    1,
                    pageSize,
                    passbookFilter,
                    passbookSearch,
                    passbookDateFrom,
                    passbookDateTo,
                  );
                }}
              />
            )}
          </div>
        )}

        <TransactionDetailsDrawer
          open={transactionDrawerOpen}
          transaction={selectedTransaction}
          onClose={() => {
            setTransactionDrawerOpen(false);
            setSelectedTransaction(null);
          }}
        />
      </div>
    </div>
  );
};

export default PlayerDetailsPage;
